import {
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/form-control'
import {
  Flex,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { Input } from '@chakra-ui/input'
import { HStack, VStack } from '@chakra-ui/layout'
import { BsInfoCircleFill } from 'react-icons/bs'
import type { FormikProps } from 'formik'
import React from 'react'
import { colors } from '../../../../ui-kit'
import type { SingleSignOnSettingsForm } from '../../../interfaces'
import { Trans, useTranslation } from 'react-i18next'

const SSOSettingsForm = ({
  form: { values, handleChange, errors, touched, handleBlur },
  disabled,
}: {
  form: FormikProps<SingleSignOnSettingsForm>
  disabled: boolean
}): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <VStack spacing="2em" width="100%" alignItems="flex-start">
        <FormControl
          id="metadataXML"
          flex="1"
          isRequired
          isInvalid={Boolean(errors.metadataXML && touched.metadataXML)}
        >
          <FormLabel color={colors.appSlate}>{t('metadata_xml', 'Metadata XML')}</FormLabel>
          <Textarea
            disabled={disabled}
            id="metadataXML"
            name="metadataXML"
            value={values.metadataXML}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t('metadata_xml_placeholder', 'Paste the contents of your metadata XML file here')}
          />
          <FormErrorMessage marginBottom="-2.0408em">
            {errors.metadataXML}
          </FormErrorMessage>
        </FormControl>
        <FormControl
          id="entityId"
          flex="1"
          isInvalid={Boolean(errors.entityId && touched.entityId)}
        >
          <HStack spacing={0}>
            <FormLabel color={colors.appSlate} marginRight={0}>
              {t('entity_id', 'Entity ID')}
            </FormLabel>
            <Popover trigger="hover" placement="right">
              <PopoverTrigger>
                <Flex>
                  <IconButton
                    aria-label="Info icon"
                    icon={<BsInfoCircleFill />}
                    color={colors.appBlueyGrey}
                    size="sm"
                    variant="link"
                    marginBottom={2}
                    marginLeft={0}
                  />
                </Flex>
              </PopoverTrigger>
              <PopoverContent marginStart={2}>
                <PopoverArrow />
                <PopoverBody>
                  <Text>
                    <Trans t={t} i18nKey="entity_id_usage">
                      <Text lineHeight="1.2em">
                        Set this field only if you want to add multiple unique instances of SSO apps for ImageKit in your Identity Provider account, typically on Azure.
                      </Text>{' '}
                      <Link
                        lineHeight="1.2em"
                        href="https://docs.imagekit.io/features/single-sign-on/sso-azure-setup#creating-multiple-unique-instances-of-imagekit-sso-application-within-azure"
                        isExternal
                      >
                        Read more here
                      </Link>{' '}.
                    </Trans>
                  </Text>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
          <Input
            disabled={disabled}
            id="entityId"
            name="entityId"
            value={values.entityId}
            onChange={handleChange}
            onBlur={handleBlur}
            fontWeight="normal"
            color="inherit"
            placeholder="Optional - e.g. https://imagekit.io/saml/consume#your_imagekit_id"
            _placeholder={{ opacity: 0.8 }}
          />
          <FormErrorMessage marginBottom="-2.0408em">
            {errors.entityId}
          </FormErrorMessage>
        </FormControl>
      </VStack>
    </>
  )
}

export default SSOSettingsForm
